import React from 'react';
import { Boxes, Palette, Box, Image } from 'lucide-react';

/**
 * Компонент для ввода параметров контента и дизайна AR-проекта
 */
const ContentParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  contentTypes,
  designComplexities
}) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Boxes className="w-5 h-5 mr-2 text-blue-500" />
        Контент и дизайн
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип контента */}
        <div>
          <label htmlFor="contentType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Box className="w-4 h-4 mr-2 text-blue-500" />
            Тип 3D контента
          </label>
          <select
            id="contentType"
            name="contentType"
            value={formData.contentType}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.contentType ? 'input-error' : ''}`}
          >
            {contentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name} - {type.description}
              </option>
            ))}
          </select>
          {errors.contentType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.contentType}
            </p>
          )}
        </div>

        {/* Сложность дизайна */}
        <div>
          <label htmlFor="designComplexity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Palette className="w-4 h-4 mr-2 text-blue-500" />
            Сложность дизайна
          </label>
          <select
            id="designComplexity"
            name="designComplexity"
            value={formData.designComplexity}
            onChange={handleInputChange}
            className={`form-select w-full ${errors.designComplexity ? 'input-error' : ''}`}
          >
            {designComplexities.map((complexity) => (
              <option key={complexity.id} value={complexity.id}>
                {complexity.name} - {complexity.description}
              </option>
            ))}
          </select>
          {errors.designComplexity && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.designComplexity}
            </p>
          )}
        </div>

        {/* Количество 3D-объектов */}
        <div>
          <label htmlFor="objectCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Количество 3D-объектов
          </label>
          <input
            type="number"
            id="objectCount"
            name="objectCount"
            value={formData.objectCount}
            onChange={handleInputChange}
            min="1"
            max="200"
            className={`form-input w-full ${errors.objectCount ? 'input-error' : ''}`}
            placeholder="Введите количество"
          />
          {errors.objectCount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.objectCount}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Учитывайте все 3D-модели, которые потребуются для проекта
          </p>
        </div>
        
        {/* Наличие существующих материалов */}
        <div className="flex items-center">
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              name="hasExistingAssets"
              checked={formData.hasExistingAssets}
              onChange={handleInputChange}
              className="form-checkbox mr-2"
            />
            <span>
              У вас есть готовые 3D-модели или дизайн-макеты
            </span>
          </label>
        </div>
        
        {/* Локализация */}
        <div>
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              name="includeLocalization"
              checked={formData.includeLocalization}
              onChange={handleInputChange}
              className="form-checkbox mr-2"
            />
            <span>
              Требуется локализация на разные языки
            </span>
          </label>
        </div>

        {/* Количество языков (если включена локализация) */}
        {formData.includeLocalization && (
          <div>
            <label htmlFor="localizationLanguages" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Количество языков
            </label>
            <input
              type="number"
              id="localizationLanguages"
              name="localizationLanguages"
              value={formData.localizationLanguages}
              onChange={handleInputChange}
              min="1"
              max="50"
              className={`form-input w-full ${errors.localizationLanguages ? 'input-error' : ''}`}
              placeholder="Введите количество языков"
            />
            {errors.localizationLanguages && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.localizationLanguages}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start">
          <Image className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
              Важно о контенте для AR
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-400">
              Качество 3D-моделей и контента напрямую влияет на впечатления пользователей от AR-приложения. 
              Оптимизированные модели обеспечивают лучшую производительность, а фотореалистичные объекты создают 
              более убедительный эффект дополненной реальности.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentParametersSection;